import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StyleSheet,
  View,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from "react-native";

import { Icon } from "../Icon/Icon";
import { webTapFloor, type UiTheme } from "../theme";

/** The tap floor, where the weeks fit at it. */
const TAP = webTapFloor ?? 44;
/** The least width of a week, under the 44 floor on purpose so six weeks fit a phone: «77.5» still reads. */
const FLOOR = 38;

// The heatmap's week columns (#358): the room right of the day labels, shared
// by the weeks, 44 each where they fit, down to 38, so six weeks fit a phone.
// Past that the grid scrolls, and a chevron at its right edge says there is
// more until the last week is in view. A row's chrome is gridStyles' gridRow
// (a 2 border, an s1 padding) and the s1 gap between cells.
export function useHeatColumns(weeks: number, theme: UiTheme) {
  const [view, setView] = useState(0);
  const [content, setContent] = useState(0);
  const [x, setX] = useState(0);
  const { t } = useTranslation();
  const gap = theme.spacing.s1;
  const chrome = 2 * (2 + gap) + gap * (weeks - 1);
  const fit = view > 0 ? Math.floor((view - chrome) / weeks) : TAP;
  const more = view > 0 && content - view - x > 1;
  // A grid that fits has nothing to scroll: it must not take the drag from the pager (#412).
  const overflows = view > 0 && content > view + 1;
  return {
    /** The week column's least width, for makeGridStyles. */
    cellMin: Math.max(FLOOR, Math.min(TAP, fit)),
    /** Spread on the heatmap's horizontal ScrollView. */
    scroll: {
      onLayout: (e: LayoutChangeEvent) => setView(Math.floor(e.nativeEvent.layout.width)),
      onContentSizeChange: (w: number) => setContent(Math.floor(w)),
      onScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => setX(e.nativeEvent.contentOffset.x),
      scrollEventThrottle: 32,
      scrollEnabled: overflows,
      // A label, not a hint: Android maps a hint to a tooltip that TalkBack never reads.
      accessibilityLabel: overflows
        ? t("mobile.plan.heatMoreWeeks", { defaultValue: "There are more weeks to the right: swipe the grid sideways." })
        : undefined,
    },
    /** The edge cue, over the ScrollView's right edge; null with nothing past it. */
    cue: more ? <EdgeCue theme={theme} /> : null,
  };
}

// A shape on its own surface, so it reads over any cell; a screen reader
// reaches the hidden weeks by itself.
function EdgeCue({ theme }: { theme: UiTheme }) {
  const p = theme.palette;
  return (
    <View pointerEvents="none" style={local.edge} importantForAccessibility="no-hide-descendants" accessibilityElementsHidden>
      <View style={[local.box, { backgroundColor: p.surface, borderColor: p.borderStrong, borderRadius: theme.radius.r2, padding: theme.spacing.s1 }]}>
        <Icon name="chevron-right" size={theme.typography.sizes.sm} color={p.ink2} />
      </View>
    </View>
  );
}

const local = StyleSheet.create({
  edge: { position: "absolute", top: 0, right: 0, bottom: 0, justifyContent: "center" },
  box: { borderWidth: 1 },
});
